import React, { useState } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Paper,
  Typography,
  Tabs,
  Tab,
  Button,
  TextField,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  Checkbox,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import { useAuth } from '../context/AuthContext';
import axios from '../utils/axiosConfig';
import { toast } from 'react-hot-toast';
import Papa from 'papaparse';

const InviteMembers = ({ groupId, groups, onClose, onSuccess }) => {
  const [activeTab, setActiveTab] = useState(0);
  const [email, setEmail] = useState('');
  const [selectedGroupId, setSelectedGroupId] = useState(groupId || '');
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const { user } = useAuth();
  const [parsedEmails, setParsedEmails] = useState([]);
  const [previewData, setPreviewData] = useState(null);

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
    setMessage({ type: '', text: '' });
  };

  const handleIndividualInvite = async (e) => {
    e.preventDefault();
    try {
        console.log('Sending invitation with data:', {
            email,
            groupId: selectedGroupId,
            inviterId: user.id,
            teamId: user.team_id
        });

        const response = await axios.post('/api/group-invitations/invite', {
            email,
            groupId: selectedGroupId,
            inviterId: user.id,
            teamId: user.team_id
        });

        if (response.data.success) {
            toast.success(`Invitation sent to ${email}`);
            setEmail('');
            onSuccess?.();
        }
    } catch (error) {
        console.error('Error sending invitation:', error);
        toast.error(error.response?.data?.message || 'Failed to send invitation');
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFile(file);
      
      // Parse CSV file
      Papa.parse(file, {
        complete: (results) => {
          // Assuming first column contains emails
          const emails = results.data
            .map(row => row[0])
            .filter(email => email && email.includes('@')); // Basic email validation
          
          setParsedEmails(emails);
          setPreviewData({
            total: emails.length,
            sample: emails.slice(0, 5) // Show first 5 emails
          });
        },
        error: (error) => {
          console.error('Error parsing CSV:', error);
          toast.error('Error parsing CSV file');
        }
      });
    }
  };

  const handleBulkInvite = async (e) => {
    e.preventDefault();
    if (!selectedGroupId) {
        toast.error('Please select a group');
        return;
    }
    if (!parsedEmails.length) {
        toast.error('No valid emails found in CSV');
        return;
    }

    try {
        setLoading(true);
        toast.loading('Sending invitations... This may take a moment.');

        const response = await axios.post('/api/group-invitations/bulk', {
            emails: parsedEmails,
            groupId: selectedGroupId,
            inviterId: user?.id,
            teamId: user?.team_id
        });

        if (response.data.success) {
            toast.dismiss();
            toast.success(`Successfully sent ${response.data.results.successful} invitations`);
            setFile(null);
            setParsedEmails([]);
            setPreviewData(null);
            onSuccess?.();
        }
    } catch (error) {
        console.error('Error sending bulk invitations:', error);
        toast.dismiss();
        toast.error(error.response?.data?.message || 'Failed to send invitations');
    } finally {
        setLoading(false);
    }
  };

  return (
    <Paper elevation={0} sx={{ p: 3, border: '1px solid #e0e0e0' }}>
      <Typography variant="h6" sx={{ mb: 3 }}>
        Invite Team Members
      </Typography>

      <Tabs 
        value={activeTab} 
        onChange={handleTabChange}
        sx={{ mb: 3 }}
      >
        <Tab 
          icon={<PersonAddIcon />} 
          label="Individual Invite" 
          iconPosition="start"
        />
        <Tab 
          icon={<UploadFileIcon />} 
          label="Bulk Upload" 
          iconPosition="start"
        />
      </Tabs>

      {!groupId && (
        <FormControl fullWidth sx={{ mb: 2 }}>
          <InputLabel>Select Group</InputLabel>
          <Select
            value={selectedGroupId}
            onChange={(e) => setSelectedGroupId(e.target.value)}
            label="Select Group"
          >
            {groups?.map((group) => (
              <MenuItem key={group.id} value={group.id}>
                {group.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      )}

      {activeTab === 0 ? (
        <Box component="form" onSubmit={handleIndividualInvite}>
          <TextField
            fullWidth
            label="Email Address"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            type="email"
            sx={{ mb: 2 }}
          />
          <Box display="flex" justifyContent="flex-end" gap={2}>
            <Button
              variant="outlined"
              onClick={onClose}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              type="submit"
              disabled={loading || !email || !selectedGroupId}
              startIcon={loading ? <CircularProgress size={20} /> : <PersonAddIcon />}
            >
              Send Invitation
            </Button>
          </Box>
        </Box>
      ) : (
        <Box component="form" onSubmit={handleBulkInvite}>
          <Box sx={{ mb: 3 }}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Upload a CSV file containing email addresses in the first column.
            </Typography>
            <input
              accept=".csv"
              style={{ display: 'none' }}
              id="csv-file"
              type="file"
              onChange={handleFileChange}
            />
            <label htmlFor="csv-file">
              <Button
                variant="outlined"
                component="span"
                startIcon={<UploadFileIcon />}
                sx={{ mb: 2 }}
              >
                Choose CSV File
              </Button>
            </label>
          </Box>

          {previewData && (
            <Box sx={{ mb: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
              <Typography variant="subtitle2" gutterBottom>
                Preview ({previewData.total} emails found):
              </Typography>
              <List dense>
                {previewData.sample.map((email, index) => (
                  <ListItem key={index}>
                    <ListItemText primary={email} />
                  </ListItem>
                ))}
                {previewData.total > 5 && (
                  <ListItem>
                    <ListItemText 
                      primary={`... and ${previewData.total - 5} more`}
                      sx={{ color: 'text.secondary' }}
                    />
                  </ListItem>
                )}
              </List>
            </Box>
          )}

          <Box display="flex" justifyContent="flex-end" gap={2}>
            <Button variant="outlined" onClick={onClose}>
              Cancel
            </Button>
            <Button
              variant="contained"
              type="submit"
              disabled={!parsedEmails.length || loading || !selectedGroupId}
              startIcon={loading ? <CircularProgress size={20} /> : <UploadFileIcon />}
            >
              {loading 
                ? 'Sending Invitations...' 
                : `Send ${parsedEmails.length || 0} Invitations`}
            </Button>
          </Box>
        </Box>
      )}
    </Paper>
  );
};

InviteMembers.propTypes = {
  groupId: PropTypes.number,
  groups: PropTypes.array,
  onClose: PropTypes.func.isRequired,
  onSuccess: PropTypes.func
};

export default InviteMembers;